import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api, { formatApiError, resolveImageUrl } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  RefreshCw, ArrowLeft, Trophy, Calendar, MapPin, Flag, AlertTriangle,
  Globe, Check, X, Trash2, ExternalLink, Search, Zap, Plus, FileText,
  Activity,
} from 'lucide-react';

const TAB_DEFS = [
  { key: 'dashboard', label: 'Dashboard', icon: Activity },
  { key: 'results', label: 'Race Results', icon: Trophy },
  { key: 'upcoming', label: 'Upcoming', icon: Calendar },
  { key: 'new_tracks', label: 'New Tracks', icon: Flag },
  { key: 'candidates', label: 'Discovered', icon: Search },
  { key: 'failures', label: 'Failures', icon: AlertTriangle },
  { key: 'extract', label: 'Paste & Extract', icon: Plus },
];

function StatCard({ icon: Icon, label, value, color = '#FF3B30' }) {
  return (
    <Card className="bg-[#141414] border-[#262626] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">{label}</p>
          <p className="text-white text-2xl font-bold">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </Card>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <Card className="bg-[#141414] border-[#262626] p-10 text-center">
      <Icon className="w-12 h-12 text-gray-700 mx-auto mb-4" />
      <h3 className="text-white font-heading text-lg mb-1">{title}</h3>
      {hint && <p className="text-gray-500 text-sm">{hint}</p>}
    </Card>
  );
}

function ResultRow({ r, onDelete }) {
  return (
    <Card className="bg-[#141414] border-[#262626] p-4" data-testid={`result-${r.id}`}>
      <div className="flex gap-4">
        {r.image_url && (
          <img src={resolveImageUrl(r.image_url)} alt="" className="w-24 h-20 object-cover rounded border border-[#262626] flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-white font-bold">{r.event_name || 'Feature Race'}</h4>
            {r.is_high_profile && (
              <span className="tag bg-[#FF3B30]/20 text-[#FF3B30] px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">
                <Zap className="w-3 h-3 inline mr-1" />High Profile
              </span>
            )}
            {r.track_state && (
              <span className="tag bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-[10px]">{r.track_state}</span>
            )}
            {r.event_date && (
              <span className="text-gray-500 text-xs">{r.event_date}</span>
            )}
          </div>
          <p className="text-gray-300 text-sm mt-0.5">
            {r.track_name} {r.track_city ? `· ${r.track_city}` : ''}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            🏆 <span className="text-white">{r.winner_name || 'Unknown'}</span>
            {r.winner_car_number ? <span className="text-gray-500"> #{r.winner_car_number}</span> : ''}
          </p>
          {r.podium && r.podium.length > 1 && (
            <p className="text-gray-500 text-xs mt-1">
              {r.podium.slice(0, 3).map((p) => `${p.position}. ${p.name}${p.car_number ? ` #${p.car_number}` : ''}`).join(' · ')}
            </p>
          )}
          {r.description && <p className="text-gray-500 text-xs mt-2 line-clamp-2">{r.description}</p>}
          <div className="flex items-center gap-3 mt-2">
            <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[#FF3B30] text-xs hover:underline flex items-center gap-1">
              <ExternalLink className="w-3 h-3" />{r.source_name}
            </a>
            {r.confidence && (
              <span className="text-gray-600 text-[10px] uppercase tracking-wider">confidence: {r.confidence}</span>
            )}
          </div>
        </div>
        <div>
          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => onDelete(r.id)} data-testid={`delete-result-${r.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function UpcomingRow({ r, onDelete }) {
  return (
    <Card className="bg-[#141414] border-[#262626] p-4" data-testid={`upcoming-${r.id}`}>
      <div className="flex gap-4">
        <div className="w-14 h-14 rounded bg-[#1F1F1F] border border-[#262626] flex flex-col items-center justify-center flex-shrink-0">
          <span className="text-gray-500 text-[10px] uppercase">{r.event_date ? new Date(r.event_date + 'T00:00:00').toLocaleString('en-US', { month: 'short' }) : ''}</span>
          <span className="text-white font-bold text-lg">{r.event_date ? new Date(r.event_date + 'T00:00:00').getDate() : '?'}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-white font-bold">{r.event_name || 'Crown Vic Event'}</h4>
            {r.track_state && <span className="tag bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-[10px]">{r.track_state}</span>}
            {r.event_type && <span className="text-gray-500 text-xs uppercase">{r.event_type}</span>}
          </div>
          <p className="text-gray-300 text-sm mt-0.5 flex items-center gap-1">
            <MapPin className="w-3 h-3 text-gray-500" />{r.track_name}{r.track_city ? `, ${r.track_city}` : ''}
          </p>
          {r.class_name && <p className="text-gray-400 text-xs mt-1">Class: {r.class_name}</p>}
          {r.description && <p className="text-gray-500 text-xs mt-1 line-clamp-2">{r.description}</p>}
          <a href={r.source_url} target="_blank" rel="noreferrer" className="text-[#FF3B30] text-xs hover:underline mt-2 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />{r.source_name}
          </a>
        </div>
        <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(r.id)}>
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
}

export default function AdminRacingIntelPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [results, setResults] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [newTracks, setNewTracks] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [failures, setFailures] = useState([]);
  const [runs, setRuns] = useState([]);
  const [scraping, setScraping] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [extractForm, setExtractForm] = useState({ text: '', source_url: '', source_name: '', image_url: '' });
  const [extractResult, setExtractResult] = useState(null);
  const [extracting, setExtracting] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAll();
  }, [user, navigate]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [d, r, u, n, c, f, rn] = await Promise.all([
        api.get('/racing/dashboard'),
        api.get('/racing/results?limit=50'),
        api.get('/racing/upcoming?limit=50'),
        api.get('/racing/new-tracks?limit=20'),
        api.get('/admin/racing/candidates?status=pending&limit=100'),
        api.get('/admin/racing/failures?limit=50'),
        api.get('/admin/racing/runs?limit=10'),
      ]);
      setDashboard(d.data);
      setResults(r.data);
      setUpcoming(u.data);
      setNewTracks(n.data);
      setCandidates(c.data);
      setFailures(f.data);
      setRuns(rn.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filteredResults = useMemo(() => {
    return results.filter((r) => {
      if (stateFilter && (r.track_state || '').toUpperCase() !== stateFilter.toUpperCase()) return false;
      if (trackFilter && !(r.track_name || '').toLowerCase().includes(trackFilter.toLowerCase())) return false;
      return true;
    });
  }, [results, stateFilter, trackFilter]);

  const handleScrapeNow = async (discovery = true) => {
    setScraping(true);
    try {
      await api.post(`/admin/racing/scrape-now?discovery=${discovery}`);
      alert(`Scrape started. This typically takes 3–6 minutes. Click Refresh to check progress.`);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setScraping(false);
    }
  };

  const handleDeleteResult = async (id) => {
    if (!window.confirm('Delete this result?')) return;
    try {
      await api.delete(`/admin/racing/results/${id}`);
      setResults((prev) => prev.filter((r) => r.id !== id));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleDeleteUpcoming = async (id) => {
    if (!window.confirm('Delete this upcoming event?')) return;
    try {
      await api.delete(`/admin/racing/upcoming/${id}`);
      setUpcoming((prev) => prev.filter((r) => r.id !== id));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleDeleteNewTrack = async (id) => {
    if (!window.confirm('Delete this new track record?')) return;
    try {
      await api.delete(`/admin/racing/new-tracks/${id}`);
      setNewTracks((prev) => prev.filter((r) => r.id !== id));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleCandidate = async (id, action) => {
    try {
      await api.post(`/admin/racing/candidates/${id}/${action}`);
      setCandidates((prev) => prev.filter((c) => c.id !== id));
    } catch (err) { alert(formatApiError(err)); }
  };

  const handleExtract = async (e) => {
    e.preventDefault();
    if (!extractForm.text.trim()) { alert('Paste some text first.'); return; }
    setExtracting(true);
    setExtractResult(null);
    try {
      const res = await api.post('/admin/racing/extract-text', extractForm);
      setExtractResult(res.data);
      fetchAll();
    } catch (err) {
      setExtractResult({ error: formatApiError(err) });
    } finally {
      setExtracting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-6">
          <Link to="/admin/dashboard" className="inline-flex items-center text-gray-400 hover:text-white text-sm mb-2">
            <ArrowLeft className="w-3 h-3 mr-1" /> Back to Admin Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Racing Intel</p>
              <h1 className="font-heading font-bold text-3xl text-white">Crown Vic Intelligence Feed</h1>
              <p className="text-gray-400 text-sm mt-1">Structured results, upcoming races, and new tracks auto-aggregated every 12 hours.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchAll()} variant="outline" className="border-[#262626] text-white" data-testid="refresh-intel-btn">
                <RefreshCw className="w-4 h-4 mr-2" />Refresh
              </Button>
              <Button onClick={() => handleScrapeNow(true)} disabled={scraping} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="scrape-now-btn">
                <Zap className="w-4 h-4 mr-2" />{scraping ? 'Starting…' : 'Scrape Now'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#141414] border-b border-[#262626] sticky top-0 z-10">
        <div className="container mx-auto px-4 flex gap-1 overflow-x-auto">
          {TAB_DEFS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 transition-colors ${
                  active ? 'border-[#FF3B30] text-white' : 'border-transparent text-gray-400 hover:text-white'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />{t.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {tab === 'dashboard' && dashboard && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Trophy} label="Results (30d)" value={dashboard.counts.results_30d} color="#22c55e" />
              <StatCard icon={Calendar} label="Upcoming (60d)" value={dashboard.counts.upcoming_60d} color="#3b82f6" />
              <StatCard icon={Flag} label="New Tracks" value={dashboard.counts.new_tracks_total} color="#FF3B30" />
              <StatCard icon={Search} label="Discovered" value={candidates.length} color="#8b5cf6" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="font-heading font-bold text-lg text-white mb-3 flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-[#FF3B30]" />Latest Crown Vic Winners
                </h3>
                <div className="space-y-3">
                  {dashboard.latest_winners.length === 0 && (
                    <EmptyState icon={Trophy} title="No winners yet" hint="Run a scrape or paste race results in the Extract tab." />
                  )}
                  {dashboard.latest_winners.slice(0, 4).map((r) => (
                    <ResultRow key={r.id} r={r} onDelete={handleDeleteResult} />
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-heading font-bold text-lg text-white mb-3 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />Upcoming Crown Vic Races
                </h3>
                <div className="space-y-3">
                  {dashboard.upcoming_races.length === 0 && (
                    <EmptyState icon={Calendar} title="No upcoming races" hint="Scraper will pick up events once tracks publish their schedules." />
                  )}
                  {dashboard.upcoming_races.slice(0, 4).map((r) => (
                    <UpcomingRow key={r.id} r={r} onDelete={handleDeleteUpcoming} />
                  ))}
                </div>
              </div>
            </div>

            {dashboard.repeat_winners && dashboard.repeat_winners.length > 0 && (
              <div>
                <h3 className="font-heading font-bold text-lg text-white mb-3">Repeat Winners (last 90 days)</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {dashboard.repeat_winners.map((w, i) => (
                    <Card key={i} className="bg-[#141414] border-[#262626] p-4">
                      <p className="text-white font-bold">{w.winner_name} {w.car_number ? <span className="text-gray-500">#{w.car_number}</span> : null}</p>
                      <p className="text-[#FF3B30] text-2xl font-bold">{w.wins} wins</p>
                      <p className="text-gray-500 text-xs mt-1">{(w.tracks || []).slice(0, 3).join(' · ')}</p>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="font-heading font-bold text-lg text-white mb-3">Recent Scraper Runs</h3>
              {runs.length === 0 ? (
                <EmptyState icon={RefreshCw} title="No runs yet" hint="Click Scrape Now to trigger the first run." />
              ) : (
                <div className="space-y-2">
                  {runs.map((run) => (
                    <Card key={run.id} className="bg-[#141414] border-[#262626] p-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <p className="text-white text-sm">{new Date(run.finished_at || run.started_at).toLocaleString()}</p>
                          <p className="text-gray-500 text-xs">{Math.round(run.duration_seconds)}s · {run.sources_total} sources · {run.llm_calls} LLM calls</p>
                        </div>
                        <div className="flex gap-3 text-xs">
                          <span className="text-green-400">{run.results_inserted} results</span>
                          <span className="text-blue-400">{run.upcoming_inserted} upcoming</span>
                          <span className="text-[#FF3B30]">{run.new_tracks_inserted} new tracks</span>
                          <span className="text-purple-400">{run.discovered_candidates} discovered</span>
                          {run.failures > 0 && <span className="text-yellow-400">{run.failures} errors</span>}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'results' && (
          <div className="space-y-4">
            <div className="flex gap-3 flex-wrap">
              <Input placeholder="Filter by state (FL, GA, OH…)" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}
                className="bg-[#1F1F1F] border-[#262626] text-white max-w-[240px]" data-testid="filter-state" />
              <Input placeholder="Filter by track name" value={trackFilter} onChange={(e) => setTrackFilter(e.target.value)}
                className="bg-[#1F1F1F] border-[#262626] text-white max-w-[320px]" data-testid="filter-track" />
              <span className="text-gray-500 text-sm self-center">{filteredResults.length} result(s)</span>
            </div>
            {filteredResults.length === 0 ? (
              <EmptyState icon={Trophy} title="No results yet" hint="Run a scrape or paste text in the Extract tab." />
            ) : (
              <div className="space-y-3">
                {filteredResults.map((r) => <ResultRow key={r.id} r={r} onDelete={handleDeleteResult} />)}
              </div>
            )}
          </div>
        )}

        {tab === 'upcoming' && (
          upcoming.length === 0 ? (
            <EmptyState icon={Calendar} title="No upcoming races" hint="Events within the next 60 days will appear here." />
          ) : (
            <div className="space-y-3">{upcoming.map((r) => <UpcomingRow key={r.id} r={r} onDelete={handleDeleteUpcoming} />)}</div>
          )
        )}

        {tab === 'new_tracks' && (
          newTracks.length === 0 ? (
            <EmptyState icon={Flag} title="No new track announcements yet" hint="Tracks that add a Crown Vic division will appear here." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {newTracks.map((t) => (
                <Card key={t.id} className="bg-[#141414] border-[#262626] p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="text-white font-bold">{t.track_name || 'Unknown track'}</h4>
                    {t.track_state && <span className="tag bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded text-[10px]">{t.track_state}</span>}
                  </div>
                  <p className="text-gray-400 text-sm">{t.track_city}</p>
                  {t.description && <p className="text-gray-300 text-sm mt-2">{t.description}</p>}
                  {t.first_scheduled_race && <p className="text-[#FF3B30] text-xs mt-2">First race: {t.first_scheduled_race}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#262626]">
                    <a href={t.source_url} target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white text-xs flex items-center gap-1">
                      <ExternalLink className="w-3 h-3" />{t.source_name}
                    </a>
                    <Button size="sm" variant="ghost" className="text-red-400" onClick={() => handleDeleteNewTrack(t.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )
        )}

        {tab === 'candidates' && (
          candidates.length === 0 ? (
            <EmptyState icon={Search} title="No discovered candidates" hint="Run a scrape (with discovery on) to find new tracks mentioning Crown Vic." />
          ) : (
            <div className="space-y-3">
              {candidates.map((c) => (
                <Card key={c.id} className="bg-[#141414] border-[#262626] p-4" data-testid={`candidate-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold">{c.track_name}</p>
                      <a href={c.url} target="_blank" rel="noreferrer" className="text-[#FF3B30] text-xs hover:underline break-all">{c.url}</a>
                      <p className="text-gray-400 text-sm mt-1 line-clamp-2">{c.snippet}</p>
                      <p className="text-gray-600 text-xs mt-1">via: "{c.found_by_keyword}"</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleCandidate(c.id, 'approve')} data-testid={`approve-${c.id}`}>
                        <Check className="w-4 h-4 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="outline" className="border-[#262626] text-gray-400" onClick={() => handleCandidate(c.id, 'reject')}>
                        <X className="w-4 h-4 mr-1" />Reject
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
        )}

        {tab === 'failures' && (
          failures.length === 0 ? (
            <EmptyState icon={Globe} title="No scrape failures" hint="All sources are reachable. Nice!" />
          ) : (
            <div className="space-y-2">
              {failures.map((f) => (
                <Card key={f.id} className="bg-[#141414] border-[#262626] p-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                    <span className="text-white text-sm font-medium">{f.source_name}</span>
                    <a href={f.url} target="_blank" rel="noreferrer" className="text-gray-400 text-xs hover:underline break-all">{f.url}</a>
                    <span className="text-gray-600 text-xs ml-auto">{new Date(f.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="text-red-400 text-xs mt-1 font-mono">{f.error}</p>
                </Card>
              ))}
            </div>
          )
        )}

        {tab === 'extract' && (
          <div className="max-w-3xl">
            <Card className="bg-[#141414] border-[#262626] p-6">
              <h3 className="font-heading font-bold text-lg text-white mb-1">Paste & Extract</h3>
              <p className="text-gray-400 text-sm mb-5">
                Paste race recap text (e.g. from a Facebook post, email, or PDF) and Gemini will extract structured fields
                and save them to the right collection.
              </p>
              <form onSubmit={handleExtract} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Source Name</Label>
                    <Input value={extractForm.source_name} onChange={(e) => setExtractForm({ ...extractForm, source_name: e.target.value })}
                      placeholder="e.g. Cordele Motor Speedway FB" className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="extract-source-name" />
                  </div>
                  <div>
                    <Label>Source URL (optional)</Label>
                    <Input value={extractForm.source_url} onChange={(e) => setExtractForm({ ...extractForm, source_url: e.target.value })}
                      placeholder="https://…" className="bg-[#1F1F1F] border-[#262626] text-white" />
                  </div>
                </div>
                <div>
                  <Label>Image URL (optional)</Label>
                  <Input value={extractForm.image_url} onChange={(e) => setExtractForm({ ...extractForm, image_url: e.target.value })}
                    placeholder="https://… photo of the event" className="bg-[#1F1F1F] border-[#262626] text-white" />
                </div>
                <div>
                  <Label>Text to extract *</Label>
                  <Textarea value={extractForm.text} onChange={(e) => setExtractForm({ ...extractForm, text: e.target.value })}
                    placeholder="Paste race recap / results / schedule here…" rows={10}
                    className="bg-[#1F1F1F] border-[#262626] text-white" data-testid="extract-text" />
                </div>
                <Button type="submit" disabled={extracting} className="bg-[#FF3B30] hover:bg-[#E6352B]" data-testid="extract-submit">
                  {extracting ? 'Extracting with Gemini…' : 'Extract & Save'}
                </Button>
              </form>
              {extractResult && (
                <div className="mt-6">
                  {extractResult.error ? (
                    <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded text-sm">
                      {extractResult.error}
                      {extractResult.error.toLowerCase().includes('budget') && (
                        <p className="text-yellow-400 mt-2 text-xs">
                          Tip: your Universal LLM key balance may need to be topped up. Profile → Universal Key → Add Balance.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-[#1F1F1F] border border-[#262626] rounded p-4">
                      <p className="text-green-400 text-sm mb-2 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        {extractResult.persisted?.inserted
                          ? `Saved to "${extractResult.persisted.kind}" collection`
                          : `Extracted but skipped: ${extractResult.persisted?.skipped_reason}`}
                      </p>
                      <pre className="text-gray-300 text-xs overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(extractResult.extracted, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </Card>

            <Card className="bg-[#141414] border-[#262626] p-4 mt-4">
              <h4 className="text-white text-sm font-bold mb-2 flex items-center gap-2"><FileText className="w-4 h-4" />Why paste & extract?</h4>
              <p className="text-gray-400 text-xs leading-relaxed">
                Many results live on Facebook pages or PDFs the scraper can't reach. Paste the text here and the system will
                auto-tag the track, state, event type, winners, podium, and class, then store it in the right collection so
                it appears on the public site.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
